/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2025 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { consume } from '@lit/context';
import { t } from 'i18next';
import {
  css,
  html,
  LitElement,
  nothing,
  PropertyValueMap,
  PropertyValues,
} from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import {
  ColorOptionDto3,
  QuantityPerSizePerColorOptionDto,
  StepEnum,
} from '@/api-sdk';
import { iconCheckmark } from '@/components/icons';
import { ConfiguratorContextType, configuratorGlobal } from '@/context';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, loadingStyles, quantityTableStyles } from '@/styles';
import { AnnouncementParams } from '@/types';
import {
  announceQuantityChange,
  getQuantityA11yName,
} from '@/utils/utils.accessibility';

import { QuantityTableMixin } from './quantity-table-mixin';

import '@/components/configuration/hsp/fg-hsp-label';
import '@/components/configurator/step/quantity/fg-quantity-table-quantity-input';
import '@/components/image/fg-image';

const SHOW_QUANTITY_INCREMENT_BUTTONS_TRESHOLD = 1;

@customElement('fg-quantity-per-size-per-color-table')
export class FgQuantityPerSizePerColorTable extends QuantityTableMixin(
  LitElement,
) {
  @property({ type: Array, attribute: 'colors' })
  colors: ColorOptionDto3[] = [];

  @property({ type: Array })
  options: QuantityPerSizePerColorOptionDto[] = [];

  @property({ type: Number })
  totalQuantity = 0;

  @property({ type: Number })
  hspMaximumQuantity?: number | string | null = 0;

  @property({ type: String })
  totalQuantityDescription?: string | null;

  @property({ type: String })
  totalPrice?: string | null;

  @property({ type: Boolean })
  loading = false;

  @property({ type: String })
  a11yDescription = '';

  @state()
  showQuantityIncrementButtons = true;

  @state()
  multiSelect = false;

  @query('.wrapper.color')
  colorWrapper?: HTMLElement;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  handleQuantitySet(event: CustomEvent) {
    const { quantity, baseQueryString } = event.detail;

    if (quantity <= 0) {
      return;
    }

    const optionData = this.findOptionWithQuantityIndex(baseQueryString);

    if (!optionData) {
      return;
    }

    const { size, unitPrice, totalPrice } = optionData.option;
    const quantityIndex = optionData.quantityIndex;

    const modifiedOptions = [
      {
        queryString: baseQueryString,
        quantity,
        size,
        color: this.colors[quantityIndex]?.color,
        unitPrice,
        totalPrice,
      },
    ];

    this.handleQuantitySetAnnouncement(event, modifiedOptions);
  }

  checkTotalPriceAndAnnounceForQuantityPerSizePerColorTable(
    changedProperties: PropertyValues,
    totalPrice: string | null | undefined,
    stepType: StepEnum,
    announcementOptions: Partial<AnnouncementParams> = {},
  ) {
    if (
      !changedProperties.has('totalPrice') ||
      !this.lastEventBaseQueryString
    ) {
      return;
    }

    const previousPrice = this.previousTotalPrice;
    this.previousTotalPrice = totalPrice;

    if (!totalPrice || totalPrice === previousPrice) {
      return;
    }

    const optionData = this.findOptionWithQuantityIndex(
      this.lastEventBaseQueryString,
    );

    if (!optionData) {
      return;
    }

    const { option, quantityIndex } = optionData;
    const quantityItem = option.quantities[quantityIndex];
    const quantity = quantityItem?.value;

    if (!quantity || quantity <= 0) {
      return;
    }

    const announcementParams: AnnouncementParams = {
      ...announcementOptions,
      quantity,
      color: this.colors[quantityIndex]?.color,
      size: option.size,
      unitPrice: option.unitPrice,
      totalPrice: option.totalPrice,
    };

    announceQuantityChange(
      this as unknown as HTMLElement,
      stepType,
      announcementParams,
    );
  }

  private findOptionWithQuantityIndex(
    queryString: string,
  ): { option: any; quantityIndex: number } | null {
    for (const option of this.options) {
      const quantityIndex = option.quantities.findIndex(
        quantity => quantity.queryString === queryString,
      );

      if (quantityIndex !== -1) {
        return { option, quantityIndex };
      }
    }
    return null;
  }

  updated(changedProperties: PropertyValues) {
    this.style.setProperty(
      '--wrapper-column-count',
      this.colors.length.toString(),
    );

    const stepType = this.configuratorContext.quantityCollapsible
      ? StepEnum.quantity_per_size_per_color_collapsible
      : StepEnum.quantity_per_size_per_color;

    this.checkTotalPriceAndAnnounceForQuantityPerSizePerColorTable(
      changedProperties,
      this.totalPrice,
      stepType,
      {
        fastDelivery: Boolean(this.hspMaximumQuantity),
      },
    );
  }

  protected willUpdate(_changedProperties: PropertyValueMap<any>) {
    if (_changedProperties.has('colors')) {
      this.multiSelect = this.colors.length > 1;
    }
  }

  render() {
    return html`
      <p id="a11y-quantity-table-description" class="sr-only">
        ${this.a11yDescription}
      </p>
      <table
        class="quantities"
        aria-describedby="a11y-quantity-table-description"
      >
        <tr>
          <td>
            <div
              class="split ${classMap({
                'show-after': this.colors?.length > 4,
              })}"
            >
              <div class="left">
                <table>
                  <thead>
                    <tr>
                      <th class="amount">${t('labels.quantity')}</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td>
                        <span class="wrapper color">
                          ${this.colors.map(colorItem => {
                            const { color, image } = colorItem;

                            return html`
                              <div
                                class="${classMap({
                                  'multi-select': this.multiSelect,
                                })}"
                              >
                                <div class="image">
                                  ${image
                                    ? html`
                                        <fg-image
                                          alt="${color}"
                                          src="${image}"
                                          height="50"
                                          width="50"
                                        ></fg-image>
                                      `
                                    : nothing}
                                </div>
                                <span>${color}</span>
                              </div>
                            `;
                          })}
                        </span>
                      </td>
                    </tr>

                    ${this.options?.length > 0
                      ? this.options.map(option => {
                          const { size, quantities } = option;

                          if (
                            quantities.length >
                            SHOW_QUANTITY_INCREMENT_BUTTONS_TRESHOLD
                          ) {
                            this.showQuantityIncrementButtons = false;
                          }

                          const hasQty = quantities.some(
                            quantityItem => quantityItem.value !== 0,
                          );

                          return html`
                            <tr class="options ${classMap({ valid: hasQty })}">
                              <td class="amount">
                                <span class="icon-wrapper collapsible">
                                  <span
                                    class="checkmark ${classMap({
                                      selected: hasQty,
                                    })}"
                                  >
                                    ${iconCheckmark}
                                  </span>
                                </span>
                                <span class="wrapper">
                                  ${quantities.map((quantityItem, key) => {
                                    const {
                                      disabled,
                                      queryString,
                                      value: quantity,
                                    } = quantityItem;

                                    const color = this.colors[key]?.color;

                                    const a11yName = getQuantityA11yName(
                                      this.configuratorContext
                                        .quantityCollapsible
                                        ? StepEnum.quantity_per_size_per_color_collapsible
                                        : StepEnum.quantity_per_size_per_color,
                                      {
                                        color,
                                        size,
                                        unitPrice: option.unitPrice,
                                      },
                                    );

                                    return html`
                                      <fg-quantity-table-quantity-input
                                        optionQueryString=${queryString ?? ''}
                                        .quantity=${quantity}
                                        .disabled=${disabled}
                                        .name=${a11yName}
                                        .title=${a11yName}
                                        .showQuantityIncrementButtons=${this
                                          .showQuantityIncrementButtons}
                                        .multiSelect=${this.multiSelect}
                                        @quantity-set=${this.handleQuantitySet}
                                      ></fg-quantity-table-quantity-input>
                                    `;
                                  })}
                                </span>
                              </td>
                            </tr>
                          `;
                        })
                      : nothing}
                  </tbody>
                </table>
              </div>

              <div class="right">
                <table>
                  <thead>
                    <tr>
                      <th class="size">${t('labels.size')}</th>
                      <th class="mobile-hidden">${t('labels.pricePerItem')}</th>
                      <th class="mobile-show">${t('labels.pricePerItemMb')}</th>
                      <th>${t('labels.total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr class="spacer">
                      <td>&nbsp;</td>
                      <td>&nbsp;</td>
                      <td>&nbsp;</td>
                    </tr>
                    ${this.options?.length > 0
                      ? this.options.map(
                          option => html`
                            <tr
                              class="
                                ${classMap({
                                loading: this.loading,
                                valid: option.quantities.some(
                                  quantityItem => quantityItem.value !== 0,
                                ),
                              })}
                              "
                            >
                              <td class="size">${option.size}</td>
                              <td><span>${option.unitPrice}</span></td>
                              <td class="total-price">
                                <span>${option.totalPrice ?? '-'}</span>
                              </td>
                            </tr>
                          `,
                        )
                      : nothing}
                  </tbody>
                </table>
              </div>
            </div>
          </td>
        </tr>

        <tfoot>
          <tr
            class="
            ${classMap({
              loading: this.loading,
            })}
            "
          >
            <td>
              <table>
                <tr class="total">
                  <td>
                    ${t(
                      'content.configurator.chooseQuantityStep.totalQuantity',
                      {
                        quantity: this.totalQuantity,
                      },
                    )}
                  </td>
                  <td><span>${this.totalPrice ?? '-'}</span></td>
                </tr>
                ${this.hspMaximumQuantity
                  ? html`
                      <tr>
                        <td>
                          <fg-hsp-label
                            .hspMaximumQuantity=${this.hspMaximumQuantity}
                          ></fg-hsp-label>
                        </td>
                        <td></td>
                      </tr>
                    `
                  : nothing}
              </table>
            </td>
          </tr>
        </tfoot>
      </table>
    `;
  }

  static styles = [
    baseStyles,
    quantityTableStyles,
    loadingStyles,
    css`
      .mobile-hidden {
        display: none;
      }

      .mobile-show {
        display: table-cell;
      }

      .quantities {
        table-layout: fixed;
      }

      .quantities > tbody {
        background: transparent;
      }

      .quantities tbody > tr > td {
        border: 0;
        padding: 0;
      }

      .quantities tbody > tr.valid {
        background-color: var(
          --fg-success--light,
          var(--fg-fallback-success--light)
        );
      }

      .quantities th.amount {
        padding-left: 32px;
      }

      .quantities td.amount {
        padding-left: 0;
      }

      .split {
        background: #ffffff;
        display: flex;
        justify-content: space-between;
        position: relative;
      }

      .split table {
        width: 100%;
      }

      .split table thead th {
        background: var(--fg-gray--100, var(--fg-fallback-gray--100));
        font-size: 0.875rem;
        white-space: nowrap;
      }

      .split .left table tr.options,
      .split .right table tr:not(.spacer) td {
        border-bottom: 1px solid
          var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      .split .left table tr.options.valid,
      .split .right table tr:not(.spacer).valid td {
        border-top: 1px solid var(--fg-success, var(--fg-fallback-success));
        border-bottom: 1px solid var(--fg-success, var(--fg-fallback-success));
      }

      .split .left table tr.options.valid + tr.options.valid,
      .split .right table tr.valid + tr.valid td {
        border-top: 1px solid transparent;
      }

      .split .left table tr.options {
        width: 100%;
      }

      .split table tbody tr td {
        padding-right: 32px;
        padding-left: 32px;
      }

      .split table tbody tr:first-child td {
        border: 0;
        padding-right: 0;
      }

      .split .left {
        overflow-x: auto;
        flex-grow: 1;
      }

      .split .left table {
        display: grid;
      }

      .split .left table tr,
      .split .left table th {
        display: block;
      }

      .split::before,
      .split::after {
        content: '';
        position: absolute;
        width: 10px;
        z-index: 1;
      }

      .split::before {
        height: calc(100% - 55px);
        top: 40px;
      }

      .split::before {
        background: linear-gradient(
          270deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 1) 100%
        );
        left: 0;
      }

      .split.show-after::after {
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(46, 52, 56, 0.15) 100%
        );
        left: 50%;
      }

      .split::after {
        height: calc(100% - 115px);
        top: 115px;
      }

      .split .left {
        width: 45%;
      }

      .split .right {
        overflow-x: auto;
        width: 55%;
      }

      .split .left table tr.options,
      .split .right table tr td {
        height: 46px;
      }

      .split .right table tbody tr:first-child td {
        height: 74px;
      }

      span.icon-wrapper {
        display: block;
        clear: left;
        float: left;
        margin-top: 10px;
      }

      .wrapper {
        display: grid;
        grid-template-columns: repeat(var(--wrapper-column-count), 1fr);
        justify-items: center;
        gap: 10px;
      }

      .wrapper div {
        font-size: 0.875rem;
        margin: 0 auto;
        text-align: center;
        width: 50px;
      }

      .wrapper div.multi-select {
        width: 60px;
      }

      .wrapper .image {
        height: 50px;
        width: 50px;
      }

      .wrapper .image fg-image::part(image) {
        height: 100%;
        object-fit: contain;
        width: 100%;
      }

      .color .image + span {
        display: block;
        height: 24px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 50px;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        .split .left,
        .split .right {
          width: 50%;
        }

        .mobile-hidden {
          display: table-cell;
        }

        .mobile-show {
          display: none;
        }
      }
    `,
  ];
}
